import React, { useContext, useEffect, useMemo, useState } from "react";
import SetupContainer from "../SharedComponents/SetupContainer";
import SetupTittle from "../SharedComponents/SetupTittle";
import Box from "../SharedComponents/Box";
import SetupInput from "../SharedComponents/SetupInput";
import SetupDropDown from "../SharedComponents/SetupDropdown";
import SetupRadio from "../SharedComponents/SetupRadio";
import SubmitBtn from "../SharedComponents/SubmitBtn";
import HistoryTable from "../SharedComponents/HistoryTable";
import axios from "axios";
import Swal from "sweetalert2";
import GetLocalStorageUserId from "../../sharedComponents/GetLocalStorageuserId";
import { contextProvider } from "../SetupPagesContextApi/SetupPagesContextApi";
import { formatDeliveryDays } from "../../../api/deliveryPlanApi";

const emptyPlanCost = () => ({ selected: false, cost: "", minCost: "", maxCost: "", id: null });

const sameWeightBand = (a = {}, b = {}) =>
  Number(a.routeId) === Number(b.routeId) &&
  Number(a.itemCategoryId) === Number(b.itemCategoryId) &&
  Number(a.dimensionId) === Number(b.dimensionId) &&
  Number(a.branchId) === Number(b.branchId) &&
  Number(a.measurementUnitId) === Number(b.measurementUnitId) &&
  Number(a.minimumWeight) === Number(b.minimumWeight) &&
  Number(a.maximumWeight) === Number(b.maximumWeight) &&
  String(a.weightDescription || "") === String(b.weightDescription || "");

const WeightSetup = () => {
  const backendUrl = process.env.REACT_APP_API_URL;
  const [fetchedData, setFetchedData] = useState([]);
  const [refetch, setRefetch] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${backendUrl}Weight`)
      .then((res) => setFetchedData(res.data))
      .catch((err) => console.log(err.message));
  }, [backendUrl, refetch]);

  const initialValue = {
    companyId: 0,
    weightDescription: "",
    minimumWeight: "",
    maximumWeight: "",
    measurementUnitId: "",
    isActive: "",
    creatorId: GetLocalStorageUserId(),
    creationDate: new Date(),
    modifierId: GetLocalStorageUserId(),
    modificationDate: new Date(),
    branchId: "",
    branchName: "string",
    routeId: 0,
    itemCategoryId: 0,
    dimensionId: 0,
  };

  const [formData, setFormData] = useState(initialValue);
  const [willUpdate, setWillUpdate] = useState(false);
  const [deliveryPlans, setDeliveryPlans] = useState([]);
  const [planCosts, setPlanCosts] = useState({});

  useEffect(() => {
    axios
      .get(`${backendUrl}DeliveryPlan`)
      .then((res) => setDeliveryPlans(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDeliveryPlans([]));
  }, [backendUrl, refetch]);

  const plansForRoute = useMemo(() => {
    return (deliveryPlans || []).filter((p) => {
      const sameRoute = Number(p.routeId) === Number(formData.routeId);
      if (!sameRoute) return false;
      if (p.isActive) return true;
      return Boolean(planCosts[p.id]?.id || planCosts[p.id]?.selected);
    });
  }, [deliveryPlans, formData.routeId, planCosts]);

  const planIdsKey = plansForRoute.map((p) => p.id).join(",");

  useEffect(() => {
    if (willUpdate || !formData.routeId) return;
    setPlanCosts((prev) => {
      const next = {};
      plansForRoute.forEach((plan) => {
        next[plan.id] = {
          selected: prev[plan.id]?.selected ?? true,
          cost: prev[plan.id]?.cost ?? "",
          minCost: prev[plan.id]?.minCost ?? "",
          maxCost: prev[plan.id]?.maxCost ?? "",
          id: null,
        };
      });
      return next;
    });
    // Route change rebuilds the plan list; existing typed costs are kept when the same plan id remains.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.routeId, planIdsKey, willUpdate]);

  const update = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const updatePlanCost = (planId, patch) => {
    setPlanCosts((prev) => {
      const current = prev[planId] || emptyPlanCost();
      const nextRow = { ...current, ...patch };
      if (patch.cost !== undefined && String(patch.cost).trim() !== "") {
        nextRow.selected = true;
      }
      return { ...prev, [planId]: nextRow };
    });
  };

  const resetForm = () => {
    setWillUpdate(false);
    setFormData(initialValue);
    setPlanCosts({});
  };

  const { handleDataReload } = useContext(contextProvider);

  const selectedPlans = plansForRoute
    .map((plan) => ({ plan, row: planCosts[plan.id] || emptyPlanCost() }))
    .filter(({ row }) => row.selected);

  const formHandle = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!selectedPlans.length) {
      Swal.fire({
        title: "Select at least one delivery plan and enter its cost.",
        icon: "warning",
      });
      return;
    }

    const missingCost = selectedPlans.find(
      ({ row }) => row.cost === "" || row.cost === null || Number.isNaN(Number(row.cost)),
    );
    if (missingCost) {
      Swal.fire({
        title: `Enter a cost for ${missingCost.plan.name}.`,
        icon: "warning",
      });
      return;
    }

    const badRange = selectedPlans.find(({ row }) => {
      const cost = Number(row.cost);
      const min = row.minCost === "" || row.minCost == null ? cost : Number(row.minCost);
      const max = row.maxCost === "" || row.maxCost == null ? cost : Number(row.maxCost);
      return min > cost || max < cost || min > max;
    });
    if (badRange) {
      Swal.fire({
        title: `Lowest must be at or below cost, and highest at or above cost, for ${badRange.plan.name}.`,
        icon: "warning",
      });
      return;
    }

    const shared = {
      ...formData,
      creatorId: GetLocalStorageUserId(),
      modifierId: GetLocalStorageUserId(),
      modificationDate: new Date(),
    };

    setSaving(true);
    try {
      for (const { plan, row } of selectedPlans) {
        const payload = {
          ...shared,
          deliveryPlanId: Number(plan.id),
          cost: Number(row.cost),
          minCost:
            row.minCost === "" || row.minCost == null
              ? null
              : Number(row.minCost),
          maxCost:
            row.maxCost === "" || row.maxCost == null
              ? null
              : Number(row.maxCost),
        };
        if (willUpdate && row.id) {
          await axios.post(`${backendUrl}Weight/${row.id}`, {
            ...payload,
            id: row.id,
          });
        } else {
          await axios.post(`${backendUrl}Weight`, payload);
        }
      }

      Swal.fire({
        title: willUpdate
          ? "Weight updated successfully"
          : selectedPlans.length > 1
            ? `${selectedPlans.length} delivery plan prices created`
            : "Weight created successfully",
        icon: "success",
      });
      setRefetch((prev) => !prev);
      resetForm();
      handleDataReload();
    } catch (err) {
      Swal.fire({
        title:
          err?.response?.data ||
          err?.message ||
          "Failed to save weight prices",
        icon: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const loadBandForEdit = (data) => {
    const siblings = (fetchedData || []).filter((row) => sameWeightBand(row, data));
    const rows = siblings.length ? siblings : [data];
    const nextCosts = {};
    rows.forEach((row) => {
      const planId = Number(row.deliveryPlanId);
      if (!planId) return;
      nextCosts[planId] = {
        selected: true,
        cost: row.cost ?? "",
        minCost: row.minCost ?? "",
        maxCost: row.maxCost ?? "",
        id: row.id,
      };
    });
    setPlanCosts(nextCosts);
    update({
      ...data,
      cost: undefined,
      deliveryPlanId: undefined,
    });
    setWillUpdate(true);
  };

  return (
    <div>
      <Box top>
        <SetupTittle text="Weight" />
        <SetupContainer>
          <p className="text-stone-600 mb-5 max-w-3xl">
            Enter the kg band once. Then tick the delivery plans for that
            route and type the per-kg cost for each plan. Lowest and highest
            are the WhatsApp bargaining range (also per kg). Leave them blank
            to keep the listed cost fixed.
          </p>
          <form className="" onSubmit={formHandle}>
            <div className="grid lg:grid-cols-2 gap-y-4 grid-cols-1 lg:gap-y-[35px] lg:gap-x-[125px]">
              <SetupInput
                name="weightDescription"
                type="text"
                placeHolder="Weight description"
                allTimeValue={formData.weightDescription}
                valueUpdate={(e) =>
                  update({ weightDescription: e.target.value })
                }
              />
              <SetupInput
                name="MaximumWeight"
                type="number"
                placeHolder="Maximum weight(kg)"
                allTimeValue={formData.maximumWeight}
                valueUpdate={(e) => update({ maximumWeight: e.target.value })}
              />
              <SetupInput
                name="MinimumWeight"
                type="number"
                placeHolder="Minimum weight(kg)"
                allTimeValue={formData.minimumWeight}
                valueUpdate={(e) => update({ minimumWeight: e.target.value })}
              />
              <SetupDropDown
                dataOf="measurementUnit"
                allTimeValue={formData.measurementUnitId}
                valueUpdate={(e) =>
                  update({ measurementUnitId: e.target.value })
                }
                placeHolder="Measurement unit"
                name="MeasurementUnitId"
              ></SetupDropDown>

              <SetupDropDown
                dataOf="dimension"
                allTimeValue={formData.dimensionId}
                valueUpdate={(e) =>
                  update({ dimensionId: parseInt(e.target.value) })
                }
                placeHolder="Dimension"
                name="dimensionId"
              ></SetupDropDown>

              <SetupDropDown
                dataOf="branch"
                allTimeValue={formData.branchId}
                valueUpdate={(e) => update({ branchId: e.target.value })}
                placeHolder="Branch"
                name="branchId"
              ></SetupDropDown>

              <SetupDropDown
                name="routeName"
                placeHolder="Route name"
                dataOf="route"
                allTimeValue={formData.routeId}
                valueUpdate={(e) =>
                  update({
                    routeId: parseInt(e.target.value),
                  })
                }
              ></SetupDropDown>
              <SetupDropDown
                name="item-group"
                placeHolder="Select item Category"
                dataOf="itemCategory"
                allTimeValue={formData.itemCategoryId}
                valueUpdate={(e) =>
                  update({ itemCategoryId: parseInt(e.target.value) })
                }
              ></SetupDropDown>

              <SetupRadio
                name="isActive"
                placeHolder="Weight status?"
                allTimeValue={formData.isActive}
                valueUpdate={(data) => update({ isActive: data })}
              />
            </div>

            <div className="mt-8 rounded-2xl border border-stone-200 bg-stone-50 p-4">
              <p className="font-semibold text-stone-800">Delivery plan costs</p>
              <p className="text-sm text-stone-500 mt-1 mb-4">
                Choose the plans, then enter the listed cost plus optional
                lowest and highest for WhatsApp negotiation.
              </p>
              {!formData.routeId ? (
                <p className="text-sm text-stone-500">
                  Select a route to load its delivery plans.
                </p>
              ) : plansForRoute.length === 0 ? (
                <p className="text-sm text-stone-500">
                  No delivery plans for this route. Create them under Delivery
                  Plan first.
                </p>
              ) : (
                <div className="space-y-3">
                  {plansForRoute.map((plan) => {
                    const row = planCosts[plan.id] || emptyPlanCost();
                    return (
                      <div
                        key={plan.id}
                        className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_120px] gap-3 items-center rounded-xl border border-stone-200 bg-white px-3 py-3"
                      >
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 accent-orange-600"
                            checked={!!row.selected}
                            onChange={(e) =>
                              updatePlanCost(plan.id, {
                                selected: e.target.checked,
                              })
                            }
                          />
                          <span>
                            <span className="font-semibold text-stone-800">
                              {plan.name}
                            </span>
                            {formatDeliveryDays(plan) ? (
                              <span className="block text-sm text-stone-500">
                                {formatDeliveryDays(plan)}
                              </span>
                            ) : null}
                            {!plan.isActive ? (
                              <span className="block text-xs text-amber-700">
                                Inactive plan
                              </span>
                            ) : null}
                          </span>
                        </label>
                        {["cost", "minCost", "maxCost"].map((key) => {
                          const label =
                            key === "cost"
                              ? "Cost / kg"
                              : key === "minCost"
                                ? "Lowest / kg"
                                : "Highest / kg";
                          return (
                            <div key={key}>
                              <label
                                className="font-semibold text-sm"
                                htmlFor={`plan-${key}-${plan.id}`}
                              >
                                {label}
                              </label>
                              <input
                                id={`plan-${key}-${plan.id}`}
                                type="number"
                                min={0}
                                step="any"
                                required={key === "cost" && !!row.selected}
                                disabled={!row.selected}
                                value={row.selected ? row[key] ?? "" : ""}
                                onChange={(e) =>
                                  updatePlanCost(plan.id, {
                                    [key]:
                                      e.target.value === ""
                                        ? ""
                                        : e.target.value,
                                  })
                                }
                                className="w-full border border-stone-300 pl-3 mt-1.5 h-11 outline-none rounded-xl bg-stone-50 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/15 disabled:opacity-50"
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <SubmitBtn
              text={saving ? "Saving" : willUpdate ? "Update" : "Create"}
            />
            <SubmitBtn
              onClick={resetForm}
              styleClass={!willUpdate && "hidden"}
              text="Cancel"
            />
          </form>
        </SetupContainer>
      </Box>
      <Box>
        <SetupTittle text="History" />
        <SetupContainer>
          <HistoryTable
            editCallBack={loadBandForEdit}
            fetchedData={fetchedData}
            headers={[
              "Weight description",
              "Maximum weight(kg)",
              "Minium weight(kg)",
              "Measurement unit",
              "Dimension",
              "Item Category",
              "Plan",
              "Days",
              "Cost",
              "Lowest",
              "Highest",
              "Branch",
              "Route",
              "Status",
            ]}
            row={[
              "weightDescription",
              "maximumWeight",
              "minimumWeight",
              "measurementUnitId",
              "dimensionId",
              "itemCategoryName",
              "deliveryPlanName",
              "deliveryPlanDaysLabel",
              "cost",
              "minCost",
              "maxCost",
              "branchName",
              "routeName",
              "isActive",
            ]}
            extraFields={[
              "branchId",
              "routeId",
              "itemCategoryId",
              "dimensionId",
              "deliveryPlanId",
              "measurementUnitId",
              "minCost",
              "maxCost",
            ]}
          />
        </SetupContainer>
      </Box>
    </div>
  );
};

export default WeightSetup;
